import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface PaymentGateway {
  id: 'paystack' | 'flutterwave';
  settingKey: string;
  name: string;
  icon: string;
  description: string;
  accentColor: string;
  bgColor: string;
  borderColor: string;
}

const allGateways: PaymentGateway[] = [
  {
    id: 'paystack',
    settingKey: 'paystack',
    name: 'Paystack',
    icon: 'ri-secure-payment-line',
    description: 'Cards, bank transfer, mobile money',
    accentColor: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-500',
  },
  {
    id: 'flutterwave',
    settingKey: 'flutterwave',
    name: 'Flutterwave',
    icon: 'ri-exchange-funds-line',
    description: 'Cards, bank, mobile money, USSD',
    accentColor: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-500',
  },
];

interface SubscriptionPaymentProps {
  plan: { name: string; price: number };
  billingCycle: 'monthly' | 'yearly';
  finalPrice: number;
  userEmail: string;
  userName: string;
  userId: string;
  onSuccess: (reference: string) => void;
  onBack: () => void;
}

export default function SubscriptionPayment({
  plan,
  billingCycle,
  finalPrice,
  userEmail,
  userName,
  userId,
  onSuccess,
  onBack,
}: SubscriptionPaymentProps) {
  const [gateways, setGateways] = useState<PaymentGateway[]>([]);
  const [loadingGateways, setLoadingGateways] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState<'paystack' | 'flutterwave' | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [platformCurrency, setPlatformCurrency] = useState('USD');

  useEffect(() => {
    fetchEnabledGateways();
  }, []);

  useEffect(() => {
    // Check for payment callback
    const params = new URLSearchParams(window.location.search);
    const paymentCallback = params.get('payment_callback');
    const transactionId = params.get('transaction_id');
    const txRef = params.get('tx_ref');
    const status = params.get('status');
    const reference = params.get('reference') || params.get('trxref');

    if (paymentCallback === 'true') {
      if (transactionId || txRef) {
        verifyFlutterwavePayment(transactionId || '', txRef || '');
      } else if (reference) {
        verifyPaystackPayment(reference);
      }
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchEnabledGateways = async () => {
    setLoadingGateways(true);
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('setting_value')
        .eq('setting_key', 'payment_revenue')
        .maybeSingle();

      if (error) throw error;

      if (data?.setting_value) {
        const sv = data.setting_value as any;
        const enabledGateways = sv.enabledGateways || {};
        const filtered = allGateways.filter((gw) => enabledGateways[gw.settingKey] === true);
        setGateways(filtered);
        if (filtered.length > 0) setSelectedMethod(filtered[0].id);
        // Load platform default currency
        if (sv.defaultCurrency) setPlatformCurrency(sv.defaultCurrency);
      }
    } catch (err) {
      console.error('Failed to load gateways:', err);
    } finally {
      setLoadingGateways(false);
    }
  };

  const verifyFlutterwavePayment = async (transactionId: string, txRef: string) => {
    setProcessing(true);
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const resp = await fetch(`${supabaseUrl}/functions/v1/flutterwave-verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ transaction_id: transactionId, expected_amount: finalPrice }),
      });
      const data = await resp.json();
      if (data.status === 'success' || data.payment_status === 'successful') {
        await saveSubscription(data.tx_ref || txRef);
        onSuccess(data.tx_ref || txRef);
      } else {
        setError(data.error || 'Payment verification failed. Please contact support.');
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setProcessing(false);
    }
  };

  const verifyPaystackPayment = async (reference: string) => {
    setProcessing(true);
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const resp = await fetch(`${supabaseUrl}/functions/v1/paystack-verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ reference }),
      });
      const data = await resp.json();
      if (data.success) {
        await saveSubscription(reference);
        onSuccess(reference);
      } else {
        setError(data.error || 'Payment verification failed.');
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setProcessing(false);
    }
  };

  const saveSubscription = async (reference: string) => {
    const now = new Date();
    const expiresAt = new Date(now);
    if (billingCycle === 'monthly') {
      expiresAt.setMonth(expiresAt.getMonth() + 1);
    } else {
      expiresAt.setFullYear(expiresAt.getFullYear() + 1);
    }

    await supabase.from('subscriptions').insert({
      user_id: userId,
      plan: plan.name.toLowerCase().replace(/\s+/g, '_'),
      billing_cycle: billingCycle,
      status: 'active',
      started_at: now.toISOString(),
      expires_at: expiresAt.toISOString(),
    });
  };

  const handlePay = async () => {
    if (!selectedMethod) {
      setError('Please select a payment method');
      return;
    }

    setError('');
    setProcessing(true);

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const callbackUrl = `${window.location.origin}${window.location.pathname}?payment_callback=true`;

      if (selectedMethod === 'flutterwave') {
        const txRef = `SUB-${plan.name.toUpperCase().replace(/\s+/g, '')}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

        console.log('🚀 Initializing Flutterwave payment...');
        console.log('Amount:', finalPrice, 'Currency:', platformCurrency);
        console.log('Email:', userEmail);
        console.log('Transaction Ref:', txRef);

        const resp = await fetch(`${supabaseUrl}/functions/v1/flutterwave-initialize`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            amount: finalPrice,
            currency: platformCurrency,
            email: userEmail,
            name: userName,
            tx_ref: txRef,
            redirect_url: callbackUrl,
            metadata: { plan: plan.name, billing_cycle: billingCycle, user_id: userId },
          }),
        });

        const data = await resp.json();
        
        console.log('📡 Flutterwave Response Status:', resp.status);
        console.log('📦 Flutterwave Response Data:', data);
        
        if (!resp.ok) {
          console.error('❌ Server returned error:', resp.status);
          console.error('Error details:', data);
          throw new Error(data.error || `Server error: ${resp.status}`);
        }
        
        if (!data.success) {
          console.error('❌ Payment initialization failed');
          console.error('Error:', data.error);
          console.error('Debug info:', data.debug);
          throw new Error(data.error || 'Payment initialization failed');
        }
        
        if (!data.payment_link) {
          console.error('❌ No payment link received');
          throw new Error('Payment link not received from Flutterwave');
        }
        
        console.log('✅ Redirecting to Flutterwave:', data.payment_link);
        window.location.href = data.payment_link;
        return;
        
      } else if (selectedMethod === 'paystack') {
        console.log('🚀 Initializing Paystack payment...');
        console.log('Amount:', finalPrice, 'Currency:', platformCurrency);
        console.log('Email:', userEmail);

        const resp = await fetch(`${supabaseUrl}/functions/v1/paystack-initialize`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            amount: finalPrice,
            email: userEmail,
            currency: platformCurrency,
            callback_url: callbackUrl,
            metadata: { plan: plan.name, billing_cycle: billingCycle, user_id: userId },
          }),
        });

        const data = await resp.json();
        
        console.log('📡 Paystack Response Status:', resp.status);
        console.log('📦 Paystack Response Data:', data);
        
        if (!resp.ok) {
          console.error('❌ Server returned error:', resp.status);
          console.error('Error details:', data);
          throw new Error(data.error || `Server error: ${resp.status}`);
        }
        
        if (!data.success) {
          console.error('❌ Payment initialization failed');
          console.error('Error:', data.error);
          throw new Error(data.error || 'Payment initialization failed');
        }
        
        if (!data.authorization_url) {
          console.error('❌ No payment URL received');
          throw new Error('Payment URL not received from Paystack');
        }
        
        console.log('✅ Redirecting to Paystack:', data.authorization_url);
        window.location.href = data.authorization_url;
        return;
      }
    } catch (err: any) {
      console.error('❌ Payment Error:', err);
      setError(err.message || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  if (loadingGateways || processing) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-3">
        <div className="w-10 h-10 border-3 border-teal-200 border-t-teal-500 rounded-full animate-spin"></div>
        <p className="text-sm text-gray-500">
          {processing ? 'Verifying your payment...' : 'Loading payment options...'}
        </p>
      </div>
    );
  }

  if (gateways.length === 0) {
    return (
      <div className="space-y-6">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center space-y-3">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
            <i className="ri-error-warning-line text-2xl text-amber-600"></i>
          </div>
          <h3 className="text-base font-semibold text-amber-900">No Payment Methods Available</h3>
          <p className="text-sm text-amber-700">
            Payment gateways have not been enabled yet. Please contact the administrator.
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium cursor-pointer whitespace-nowrap"
        >
          Go Back
        </button>
      </div>
    );
  }

  const activeGateway = gateways.find((g) => g.id === selectedMethod);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Payment Summary */}
      <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl p-4 sm:p-6 text-white">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-vip-crown-line text-xl"></i>
            </div>
            <div>
              <p className="text-xs sm:text-sm text-white/80">{plan.name} Plan — {billingCycle === 'monthly' ? 'Monthly' : 'Yearly'}</p>
              <p className="text-xl sm:text-2xl font-bold">{platformCurrency} {finalPrice.toFixed(2)}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <i className="ri-shield-check-line text-white/80"></i>
            <span className="text-xs text-white/80">Secure</span>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 flex items-start gap-2 sm:gap-3">
          <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-error-warning-line text-red-600 text-xs sm:text-sm"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm font-medium text-red-900">Payment Error</p>
            <p className="text-xs sm:text-sm text-red-700 mt-0.5 break-words">{error}</p>
          </div>
        </div>
      )}

      {/* Gateway Selection */}
      <div>
        <h3 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Choose Payment Method</h3>
        <div className={`grid gap-2 sm:gap-3 ${gateways.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
          {gateways.map((gw) => {
            const isSelected = selectedMethod === gw.id;
            return (
              <button
                key={gw.id}
                type="button"
                onClick={() => { setSelectedMethod(gw.id); setError(''); }}
                className={`relative flex items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-3 sm:py-3.5 rounded-xl border-2 transition-all duration-200 cursor-pointer text-left ${
                  isSelected
                    ? `${gw.borderColor} ${gw.bgColor} shadow-sm`
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {isSelected && (
                  <div className="absolute top-2 right-2">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 bg-teal-500 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                  </div>
                )}
                <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? gw.bgColor : 'bg-gray-100'}`}>
                  <i className={`${gw.icon} text-base sm:text-lg ${isSelected ? gw.accentColor : 'text-gray-400'}`}></i>
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-xs sm:text-sm font-semibold ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>{gw.name}</p>
                  <p className="text-xs text-gray-500 truncate">{gw.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Gateway Info */}
      {selectedMethod === 'flutterwave' && (
        <div className="bg-amber-50 rounded-xl p-4 sm:p-5 border border-amber-100 text-center space-y-2 sm:space-y-3">
          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto">
            <i className="ri-exchange-funds-line text-xl sm:text-2xl text-amber-600"></i>
          </div>
          <p className="text-xs sm:text-sm text-gray-600">
            You'll be redirected to Flutterwave to pay <strong>{platformCurrency} {finalPrice.toFixed(2)}</strong> securely.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1"><i className="ri-bank-card-line"></i> Cards</span>
            <span className="flex items-center gap-1"><i className="ri-smartphone-line"></i> Mobile Money</span>
            <span className="flex items-center gap-1"><i className="ri-bank-line"></i> Bank</span>
          </div>
        </div>
      )}

      {selectedMethod === 'paystack' && (
        <div className="bg-emerald-50 rounded-xl p-4 sm:p-5 border border-emerald-100 text-center space-y-2 sm:space-y-3">
          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto">
            <i className="ri-secure-payment-line text-xl sm:text-2xl text-emerald-600"></i>
          </div>
          <p className="text-xs sm:text-sm text-gray-600">
            You'll be redirected to Paystack to pay <strong>{platformCurrency} {finalPrice.toFixed(2)}</strong> securely.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1"><i className="ri-bank-card-line"></i> Cards</span>
            <span className="flex items-center gap-1"><i className="ri-bank-line"></i> Bank Transfer</span>
            <span className="flex items-center gap-1"><i className="ri-smartphone-line"></i> Mobile Money</span>
          </div>
        </div>
      )}

      {/* Security */}
      <div className="flex items-start gap-2 sm:gap-3 bg-gray-50 rounded-lg p-3 sm:p-4">
        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
          <i className="ri-lock-line text-teal-600 text-xs sm:text-sm"></i>
        </div>
        <p className="text-xs text-gray-500 leading-relaxed">
          Your payment is encrypted and secure. Your subscription activates immediately after successful payment.
        </p>
      </div>

      {/* Tax Disclaimer */}
      <div className="flex items-start gap-2 sm:gap-3 bg-amber-50 border border-amber-100 rounded-lg p-3 sm:p-4">
        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
          <i className="ri-receipt-line text-amber-600 text-xs sm:text-sm"></i>
        </div>
        <p className="text-xs text-amber-700 leading-relaxed">
          Prices shown may be subject to local taxes. Sellers and organizers on this platform are solely responsible for their own tax obligations.
        </p>
      </div>

      {/* Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        <button
          type="button"
          onClick={onBack}
          className="w-full sm:flex-1 px-4 sm:px-6 py-2.5 sm:py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium cursor-pointer whitespace-nowrap text-sm"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handlePay}
          disabled={processing}
          className="w-full sm:flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-600 hover:to-cyan-600 shadow-lg shadow-teal-500/25 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          <i className={`${activeGateway?.icon || 'ri-lock-line'}`}></i>
          Pay {platformCurrency} {finalPrice.toFixed(2)}
        </button>
      </div>
    </div>
  );
}
